import React, { useState } from 'react';
import {
  IAuditoriaCircularidadeTransitoriaResultado,
  IAuditoriaCircularidadeTransitoriaItem,
} from '../interfaces/IAuditoriaCircularidadeTransitoria';

interface Props {
  resultado: IAuditoriaCircularidadeTransitoriaResultado;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ERRO'   ? 'badge-light-danger' :
    status === 'ALERTA' ? 'badge-light-warning' :
    status === 'AVISO'  ? 'badge-light-warning' :
                          'badge-light-success';
  return <span className={`badge ${cls}`}>{status}</span>;
};

const TipoBadge: React.FC<{ tipo: string }> = ({ tipo }) => {
  const cls = tipo === 'GIRO_CIRCULAR' ? 'badge-light-danger' : 'badge-light-warning';
  const label = tipo === 'GIRO_CIRCULAR' ? 'Giro Circular' : 'Saldo Aberto';
  return <span className={`badge ${cls}`}>{label}</span>;
};

// COR-202: status de documentação — separa o indício contábil (badge de Status acima) da
// conclusão documental, que a auditoria não tem como confirmar sozinha (não consulta nenhum
// repositório de documentos).
const StatusDocumentacaoBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'CONTRATO_LOCALIZADO'      ? 'badge-light-success' :
    status === 'CONTRATO_NAO_LOCALIZADO'  ? 'badge-light-danger' :
    status === 'PENDENTE_VALIDACAO_MANUAL' ? 'badge-light-warning' :
                                             'badge-light-secondary'; // NAO_ANALISADO
  const label =
    status === 'CONTRATO_LOCALIZADO'      ? 'Contrato Localizado' :
    status === 'CONTRATO_NAO_LOCALIZADO'  ? 'Contrato Não Localizado' :
    status === 'PENDENTE_VALIDACAO_MANUAL' ? 'Pendente de Validação Manual' :
                                             'Documentação Não Analisada';
  return <span className={`badge ${cls}`}>{label}</span>;
};

// COR-259: a ECD só evidencia a descrição da conta e o histórico — nunca confirma, por si só,
// vínculo societário (parte relacionada/inter-company). Este badge separa a keyword contábil
// (que só identifica o MÚTUO) da confirmação societária, obtida por cruzamento com o QSA.
const VinculoSocietarioBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'CONFIRMADO'     ? 'badge-light-success' :
    status === 'NAO_CONFIRMADO' ? 'badge-light-warning' :
                                  'badge-light-secondary'; // PENDENTE
  const label =
    status === 'CONFIRMADO'     ? 'Vínculo Confirmado' :
    status === 'NAO_CONFIRMADO' ? 'Vínculo Não Confirmado' :
                                  'Vínculo Pendente (QSA indisponível)';
  return <span className={`badge ${cls}`}>{label}</span>;
};

const ItemRow: React.FC<{ item: IAuditoriaCircularidadeTransitoriaItem }> = ({ item }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr>
        <td>
          <button
            className='btn btn-sm btn-icon btn-light me-2'
            onClick={() => setExpanded(!expanded)}
            title={expanded ? 'Recolher' : 'Expandir'}
          >
            <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`} />
          </button>
          <span className='fw-bold'>{item.empresaNome}</span>
          <br />
          <span className='text-muted fs-8'>{item.cnpj}</span>
        </td>
        <td>
          <span className='fw-bold fs-8'>{item.contaReferencial}</span>
          <br />
          <span className='text-muted fs-8'>{item.descricaoConta}</span>
        </td>
        <td className='text-center'><TipoBadge tipo={item.tipoOcorrencia} /></td>
        <td className='text-end fw-bold'>{formatCurrency(item.totalMovimento)}</td>
        <td className='text-end fw-bold text-success'>{formatCurrency(item.passivoTributario)}</td>
        <td className='text-center'><StatusBadge status={item.status} /></td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={6} className='bg-light'>
            <div className='p-4'>
              {/* COR-250: saldo inicial/final REAIS da ECD, separados do movimento do período —
                  é o Saldo Final que determina se a conta está "em aberto", não o movimento. */}
              <div className='row g-3 mb-3'>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Saldo Inicial (ECD)</div>
                    <div className='fw-bolder fs-5'>{formatCurrency(item.saldoInicialEcd)} {item.naturezaSaldoInicial}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Total Débitos (período)</div>
                    <div className='fw-bolder fs-5'>{formatCurrency(item.totalDebitos)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Total Créditos (período)</div>
                    <div className='fw-bolder fs-5'>{formatCurrency(item.totalCreditos)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Saldo Final (ECD)</div>
                    <div className='fw-bolder fs-5'>{formatCurrency(item.saldoFinalEcd)} {item.naturezaSaldoFinal}</div>
                  </div>
                </div>
              </div>

              <div className='row g-3 mb-3'>
                <div className='col-md-4'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Movimento Líquido (período)</div>
                    <div className='fw-bolder fs-5'>{formatCurrency(item.saldoLiquido)}</div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Qtd. Lançamentos</div>
                    <div className='fw-bolder fs-5'>{item.quantidadeLancamentos}</div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Base de Cálculo (BC)</div>
                    <div className='fw-bolder fs-5'>{formatCurrency(item.tipoOcorrencia === 'GIRO_CIRCULAR' ? item.totalMovimento / 2 : item.saldoFinalEcd)}</div>
                  </div>
                </div>
              </div>

              {item.baseCalculoUtilizada && (
                <div className='alert alert-light-primary border border-primary p-3 fs-8 mb-3'>
                  <strong>Base usada no cálculo:</strong> {item.baseCalculoUtilizada}
                </div>
              )}

              {/* COR-258: separa o saldo herdado de exercícios anteriores (pendência de
                  documentação/aging, sem cálculo de passivo do período) do saldo efetivamente
                  constituído no período auditado (única base elegível para a referência de juros
                  presumidos abaixo) — só se aplica a Saldo Aberto. */}
              {item.tipoOcorrencia === 'SALDO_ABERTO' && (
                <div className='row g-3 mb-3'>
                  <div className='col-md-6'>
                    <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                      <div className='text-muted fs-8'>Saldo Herdado de Exercícios Anteriores</div>
                      <div className='fw-bolder fs-5'>{formatCurrency(item.saldoHerdadoDeExerciciosAnteriores)}</div>
                      <div className='text-muted' style={{ fontSize: 10 }}>Pendência de documentação/aging — sem passivo do período</div>
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                      <div className='text-muted fs-8'>Saldo Novo Constituído no Período</div>
                      <div className='fw-bolder fs-5'>{formatCurrency(item.saldoNovoConstituidoNoPeriodo)}</div>
                      <div className='text-muted' style={{ fontSize: 10 }}>Única base elegível para a referência de juros abaixo</div>
                    </div>
                  </div>
                </div>
              )}

              {/* COR-256: passivo CONFIRMADO — sempre R$ 0,00, pois StatusDocumentacao é sempre
                  "NAO_ANALISADO" (nenhuma confirmação documental disponível nesta plataforma). */}
              <div className='row g-3 mb-3'>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>IRPJ/CSLL (34%) — Confirmado</div>
                    <div className='fw-bolder fs-5 text-success'>{formatCurrency(item.tributosIrpjCsll)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Multa Art. 44 (75%) — Confirmado</div>
                    <div className='fw-bolder fs-5 text-success'>{formatCurrency(item.multaArt44)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Juros SELIC — Confirmado</div>
                    <div className='fw-bolder fs-5 text-success'>{formatCurrency(item.jurosSelic)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Passivo Confirmado</div>
                    <div className='fw-bolder fs-5 text-success'>{formatCurrency(item.passivoTributario)}</div>
                  </div>
                </div>
              </div>

              {/* COR-257/258: referência informativa — juros presumidos (RIR/2018 Art. 579) só
                  sobre o saldo novo constituído no período, nunca sobre o principal nem sobre
                  saldo herdado. Nunca confirmada automaticamente; exibida separadamente do
                  passivo confirmado acima. */}
              {item.tipoOcorrencia === 'SALDO_ABERTO' && item.passivoReferencia > 0 && (
                <div className='card border-0 p-3 mb-3' style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                  <div className='mb-2'>
                    <strong>Referência Informativa (não confirmada)</strong>
                    <div className='text-muted' style={{ fontSize: 11 }}>
                      Juros mínimos (Selic) que deveriam ter sido cobrados sobre o saldo constituído no período —
                      nunca sobre o principal do mútuo. Depende de validação documental para confirmação fiscal.
                    </div>
                  </div>
                  <div className='row g-3'>
                    <div className='col-md-3 text-center'>
                      <div className='text-muted' style={{ fontSize: 10 }}>Juros Presumidos</div>
                      <div className='fw-bolder fs-6'>{formatCurrency(item.jurosPresumidosReferencia)}</div>
                    </div>
                    <div className='col-md-3 text-center'>
                      <div className='text-muted' style={{ fontSize: 10 }}>IRPJ/CSLL (34%)</div>
                      <div className='fw-bolder fs-6'>{formatCurrency(item.tributosIrpjCsllReferencia)}</div>
                    </div>
                    <div className='col-md-3 text-center'>
                      <div className='text-muted' style={{ fontSize: 10 }}>Multa 75% + SELIC</div>
                      <div className='fw-bolder fs-6'>{formatCurrency(item.multaReferencia + item.jurosSelicReferencia)}</div>
                    </div>
                    <div className='col-md-3 text-center'>
                      <div className='text-muted' style={{ fontSize: 10 }}>Passivo de Referência</div>
                      <div className='fw-bolder fs-6'>{formatCurrency(item.passivoReferencia)}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* COR-260: motivo da severidade exposto objetivamente — tipo, valor, documentação
                  e vínculo societário, nunca misturados num "ERRO" genérico. */}
              {item.motivoSeveridade && (
                <div className='alert alert-light-primary border border-primary p-3 fs-8 mb-3'>
                  <strong>Motivo da Severidade:</strong> {item.motivoSeveridade}
                </div>
              )}

              <div className='alert alert-light-warning border border-warning p-3 fs-8 mb-3'>
                {item.mensagemDiagnostico}
              </div>

              <div className='card border-0 bg-white shadow-sm p-3 mb-3'>
                <div className='d-flex align-items-center gap-2 mb-2 flex-wrap'>
                  <span className='text-muted fs-8'>Status da Documentação:</span>
                  <StatusDocumentacaoBadge status={item.statusDocumentacao} />
                  <span className='text-muted fs-8 ms-3'>Vínculo Societário:</span>
                  <VinculoSocietarioBadge status={item.vinculoSocietario} />
                </div>
                {item.descricaoStatusDocumentacao && (
                  <div className='text-muted fs-9'>{item.descricaoStatusDocumentacao}</div>
                )}
                {item.descricaoVinculoSocietario && (
                  <div className='text-muted fs-9 mt-1'>{item.descricaoVinculoSocietario}</div>
                )}
              </div>

              {item.lancamentos && item.lancamentos.length > 0 && (
                <div className='table-responsive'>
                  <table className='table table-sm table-row-gray-200 align-middle gs-0 gy-2'>
                    <thead>
                      <tr className='fw-bolder text-muted'>
                        <th>Data</th>
                        <th>Histórico</th>
                        <th className='text-center'>Tipo</th>
                        <th className='text-end'>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.lancamentos.map((lanc, j) => (
                        <tr key={j}>
                          <td className='fs-8'>{lanc.dataLancamento}</td>
                          <td className='fs-8 text-muted'>{lanc.historico}</td>
                          <td className='text-center'>
                            <span className={`badge ${lanc.tipoLancamento === 'D' ? 'badge-light-danger' : 'badge-light-success'}`}>
                              {lanc.tipoLancamento === 'D' ? 'Débito' : 'Crédito'}
                            </span>
                          </td>
                          <td className='text-end fs-8 fw-bold'>{formatCurrency(lanc.valor)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const AuditoriaCircularidadeTransitoriaDetalhe: React.FC<Props> = ({ resultado }) => {
  if (!resultado) return null;

  return (
    <div className='card card-custom card-stretch mb-5'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-4 mb-1'>
            Detalhamento — Circularidade de Fluxo em Contas Transitórias Inter-company
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            {resultado.itens.length} ocorrência(s) detectada(s) · Passivo total confirmado:{' '}
            <strong className='text-success'>{formatCurrency(resultado.passivoTributarioTotal)}</strong>
            {resultado.passivoReferenciaTotal > 0 && (
              <> · Referência informativa (não confirmada): <strong>{formatCurrency(resultado.passivoReferenciaTotal)}</strong></>
            )}
          </span>
        </h3>
      </div>

      {resultado.mensagemDirecionamento && (
        <div className='card-body pb-0'>
          <div className={`alert p-3 fs-8 ${
            resultado.status === 'ERRO'   ? 'alert-light-danger border border-danger' :
            resultado.status === 'ALERTA' ? 'alert-light-warning border border-warning' :
                                            'alert-light-warning border border-warning'
          }`}>
            {resultado.mensagemDirecionamento}
          </div>
        </div>
      )}

      {/* COR-259: transparência da consulta ao QSA usada para tentar confirmar vínculo
          societário — mesmo padrão de Benefícios Indiretos (COR-233). */}
      {resultado.mensagemQsa && (
        <div className='card-body pb-0'>
          <div className={`alert p-3 fs-8 ${resultado.qsaDisponivel ? 'alert-light-secondary' : 'alert-light-warning border border-warning'}`}>
            <strong>QSA consultado na Receita Federal:</strong> {resultado.mensagemQsa}
          </div>
        </div>
      )}

      <div className='card-body pt-3'>
        {resultado.itens.length === 0 ? (
          <div className='text-success fw-bold fs-5 text-center py-8'>
            ✓ Nenhuma circularidade de fluxo em contas transitórias inter-company identificada. Situação regular.
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Empresa</th>
                  <th>Conta</th>
                  <th className='text-center'>Tipo</th>
                  <th className='text-end'>Movimento Total</th>
                  <th className='text-end'>Passivo Confirmado</th>
                  <th className='text-center'>Status</th>
                </tr>
              </thead>
              <tbody>
                {resultado.itens.map((item, i) => (
                  <ItemRow key={i} item={item} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {resultado.fundamentacaoLegal && (
          <div className='alert alert-light border border-secondary p-3 fs-9 mt-4'>
            <strong>Fundamentação Legal:</strong> {resultado.fundamentacaoLegal}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditoriaCircularidadeTransitoriaDetalhe;
